import {
  Component,
  ViewEncapsulation,
  Inject,
  PLATFORM_ID,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CountryToken, EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { PriceGuideComponent } from '../priceGuide.component';
import { SEOService } from '@services/logic/seo.service';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { ExternalService } from '@app/services/http/external.service';
import { BraggingLineComponent } from '@components/seoPageElements/bragging-line/bragging-line.component';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { PriceAverageComponent } from '@app/pages/guides/priceAverage/priceAverage.component';
import { PriceGuidesPriceSuggesterComponent } from '@components/guides/priceGuidePriceSuggester/priceGuidePriceSuggester';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ScrollProgressBarDirective } from '@app/directives/scroll-progress-bar-directive';
import { GenericButtonWithArrow } from '@components/shared/genericButtonWithArrow/genericButtonWithArrow';
import { takeUntil } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { PriceGuideService } from '@services/http/priceGuide.service';
import { AIPriceGuide, TaskOffer } from '@models/guides/aiPriceGuide.model';
import { ImageViewerComponent } from '@components/shared/image-viewer/image-viewer.component';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { SvgStarComponent } from '@components/seoPageElements/svg-star/svg-star.component';
import { SecurityContext } from '@angular/core';
import { SafePipe } from '@app/pipes/safe';
import { SafeHtml } from '@angular/platform-browser';
import { EntityCategoryPreFixHelper } from '@helpers/entityCategoryPreFixHelper';
import { RelatedGuides } from '@app/pages/guides/aiPriceGuidesOverview/interface/aiPriceGuideOverview.type.interface';
import { setBreadCrumb } from '@utils/setBreadCrumb';
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { RedirectHandlerService } from '@app/services/redirect-handler.service';
import { ServerSideResponseService } from '@services/logic/serverSideResponse.service';
import { isPlatformBrowser } from '@angular/common';
import { SimilarTasksComponent } from '@components/seoPageElements/similar-tasks/similar-tasks.component';
import { getSeoCountryConfig } from '@app/pages/seo/country-config';
import type { DisplayPrice } from '@models/vertical/displayPrice.model';
import {
  composeTitleWithPrice,
  containsPriceFigure,
  formatTitlePriceToken,
  stripAuthoredPriceTail,
} from '@utils/displayPriceToken';
import { FormatCurrencyPipe } from '@app/pipes/format-currency.pipe';
import { FormatCurrencyRangePipe } from '@app/pipes/format-currency-range.pipe';

/**
 * A stem that already closes a sentence, optionally through a closing quote or
 * bracket. 9,040 of the 9,870 indexable keepers end in «?», where a lower-case
 * token would render «... hæk? fra 350 kr.» and read as a typo.
 */
const SENTENCE_END = /[?!.]["»)\]]?$/;

@Component({
  selector: 'app-ai-price-guide',
  templateUrl: './ai-price-guide.component.html',
  styleUrls: ['../priceGuideCss.scss', './ai-price-guide.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { ngSkipHydration: 'true' },
  standalone: true,
  imports: [
    FormatCurrencyPipe,
    FormatCurrencyRangePipe,
    BraggingLineComponent,
    CommonModule,
    MatIconModule,
    BreadcrumbListComponent,
    PriceAverageComponent,
    PriceGuidesPriceSuggesterComponent,
    TranslatePipe,
    ScrollProgressBarDirective,
    GenericButtonWithArrow,
    ImageViewerComponent,
    FooterComponent,
    SvgStarComponent,
    NgOptimizedImage,
    SafePipe,
    RouterModule,
    JsonLdPipe,
    SimilarTasksComponent,
  ],
  providers: [SEOService, TranslatePipe, ExternalService],
})
export class AiPriceGuideComponent extends PriceGuideComponent {
  protected override readonly env = inject(EnvToken);
  protected override readonly seg = inject(SeoSegmentsToken);
  protected override readonly country = inject(CountryToken);

  aiPriceGuide?: AIPriceGuide;

  private readonly seoCountryConfig = getSeoCountryConfig(this.country);

  private readonly aiImageMap: Record<string, string> = {
    nl: `${this.env.cloudFrontImgUrl}/private/lp/webp/happy_woman_with_phone_nl.webp`,
    dk: `${this.env.cloudFrontImgUrl}/private/lp/webp/lp_hero_imgs/happy_woman_with_phone.webp`,
  };

  womanWithPhone = this.aiImageMap[this.country];
  private routeMapUrl: string = '';
  private readonly MAP_CACHE_PREFIX = 'map_url_';
  private readonly CACHE_EXPIRY_HOURS = 24;
  private _taskOfferUsers: any[] = [];
  public relatedGuides: RelatedGuides[] = [];
  public override partsOfUrl: { entity?: any; name: string; url: string }[] =
    [];

  constructor(
    public override activatedRoute: ActivatedRoute,
    public override priceGuideService: PriceGuideService,
    @Inject(PLATFORM_ID) platformId: Object,
    public override router: Router,
    public override seoService: SEOService,
    public override sanitizer: DomSanitizer,
    public override t: TranslatePipe,
    public override externalService: ExternalService,
    private cdr: ChangeDetectorRef,
    private redirectHandler: RedirectHandlerService,
    protected override serverSideResponseService: ServerSideResponseService,
  ) {
    super(
      activatedRoute,
      externalService,
      priceGuideService,
      platformId,
      router,
      seoService,
      sanitizer,
      t,
      serverSideResponseService,
    );
  }
  private _categoryTitle: string = '';
  private _whoCanHireSection = { body: '', title: '' };
  private _faqSection: any[] = [];
  private _marketInsights: {
    title: string;
    stats: any | null;
    content: string;
  } = { title: '', stats: null, content: '' };
  private _richTextSection: {
    title: string;
    content: string | SafeHtml;
  } = { title: '', content: '' };
  private _similarTasks: any[] = [];

  get categoryTitle() {
    if (!this._categoryTitle && this.aiPriceGuide?.relatedCategory?.title) {
      this._categoryTitle = this.aiPriceGuide.relatedCategory.title;
      this.cdr.markForCheck();
    }
    return this._categoryTitle;
  }

  get whoCanHireSection() {
    if (!this._whoCanHireSection.body && this.aiPriceGuide) {
      const body = (this.aiPriceGuide.whoICanHireBody || '')
        .replace(/```html/g, '')
        .replace(/```/g, '');
      this._whoCanHireSection = {
        body,
        title: this.aiPriceGuide.whoICanHireH2Title || '',
      };
      this.cdr.markForCheck();
    }
    return this._whoCanHireSection;
  }

  get faqSection() {
    if (!this._faqSection.length && this.aiPriceGuide?.content?.FAQ_area) {
      this._faqSection = this.aiPriceGuide.content.FAQ_area;
      this.cdr.markForCheck();
    }
    return this._faqSection;
  }

  get marketInsights() {
    if (!this._marketInsights.content && this.aiPriceGuide?.content) {
      this._marketInsights = {
        title: this.aiPriceGuide.content.market_insights_title || '',
        stats: this.aiPriceGuide.taskStatistics || null,
        content:
          this.aiPriceGuide.content.market_insights ||
          ''
            .replace(/<h2[^>]*>.*?<\/h2>/g, '')
            .replace(/```html/g, '')
            .replace(/```/g, ''),
      };
      this.cdr.markForCheck();
    }
    return this._marketInsights;
  }

  get richTextSection() {
    if (!this._richTextSection.content && this.aiPriceGuide?.content) {
      const htmlContent = this.aiPriceGuide.content.rich_text || '';
      const sanitizedContent = this.sanitizer.sanitize(
        SecurityContext.HTML,
        htmlContent,
      );
      this._richTextSection = {
        title: this.aiPriceGuide.content.rich_text_title || '',
        content: this.sanitizer.bypassSecurityTrustHtml(sanitizedContent || ''),
      };
      this.cdr.markForCheck();
    }
    return this._richTextSection;
  }

  get similarTasks() {
    if (!this._similarTasks.length && this.aiPriceGuide?.similarTasks) {
      this._similarTasks = [...this.aiPriceGuide.similarTasks]
        .map((task) => ({
          ...task,
          averageOfferPrice: this.calculateAverageOfferPrice(task.offers || []),
        }))
        .sort((a, b) => {
          const aHasImages = a.images && a.images.length > 0;
          const bHasImages = b.images && b.images.length > 0;

          if (aHasImages && !bHasImages) return -1;
          if (!aHasImages && bHasImages) return 1;
          return 0;
        });
      this.cdr.markForCheck();
    }
    return this._similarTasks;
  }

  private calculateAverageOfferPrice(
    offers: Array<{ priceBid: number }>,
  ): number {
    if (!offers.length) return 0;
    const sum = offers.reduce((acc, offer) => acc + offer.priceBid, 0);
    return Math.round(sum / offers.length);
  }

  get taskImages() {
    return this.aiPriceGuide?.task?.images || [];
  }

  get taskTitle() {
    return this.aiPriceGuide?.task?.title || '';
  }

  private shouldIncludeUser(user: any): boolean {
    return user && user.firstName && user.firstName.toLowerCase() !== 'deleted';
  }

  get filteredTaskOffers(): any[] {
    if (!this.aiPriceGuide?.task?.offers) return [];
    return this.aiPriceGuide.task.offers.filter((offer) =>
      this.shouldIncludeUser(offer.user),
    );
  }

  onProfileImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/assets/svg/profile_img_placeholder 70x70.png';
    }
  }

  selectedTaskImages: { url: string; alt?: string }[] = [];
  selectedImageIndex = 0;
  showImageViewer = false;

  onImgError(event: any) {
    event.target.src = `/assets/svg/logo-inverted.png`;
  }

  openImageViewer(images: string[], index: number) {
    this.selectedTaskImages = images.map((url) => ({
      url,
      alt: this.taskTitle,
    }));
    this.selectedImageIndex = index;
    this.showImageViewer = true;
  }

  closeImageViewer() {
    this.showImageViewer = false;
  }

  public override capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public override setMetaDetails() {
    this.setBreadCrumb();

    // Remove duplicate consecutive parameters from canonical URL
    const urlPath = this.router.url;
    const cleanedPath = this.removeDuplicateParams(urlPath);
    const canonicalUrl = `${this.env.domain}${cleanedPath}`;

    const composed = this.composeTitle();

    this.seoService.setPageMeta({
      metaTitle: composed.title,
      // Left as stored: the band it already carries is drawn from the same
      // `price_guide` as the title's from-price, so the two cannot contradict.
      metaDesc: this.aiPriceGuide?.meta_description || '',
      canonicalUrl: canonicalUrl,
      img: this.priceGuideImg,
      appendSiteName: composed.appendSiteName,
    });

    // Pruned pages: keep serving the page (HTTP 200), drop it from the index.
    this.seoService.setRobotsNoindex(!!this.aiPriceGuide?.noindex);
  }

  /**
   * The `<title>`, recomposed so the price survives truncation.
   *
   * These are pure price-intent pages — «hvad koster X» — and 8,573 of the
   * 9,870 indexable keepers already quote a figure. The problem is WHERE: it is
   * baked into the end of `title_tag`, which averages 59 characters before
   * SeoService appends the 12-character ` - Handyhand`, so the rendered title
   * averages ~71 against a ~60-character display cap. The price is written and
   * then cut off, and the template converts at 0.43% where mini-services manage
   * 4.73%.
   *
   * So the authored tail comes off and a budgeted token goes back on, through
   * the same brand-yields-to-price composition `/hh` and `/service` use. Across
   * the corpus that takes the mean rendered title from 71.0 to 58.7 characters
   * and over-cap titles from 98.1% to 21.3%, at no cost in published prices.
   */
  private composeTitle(): { title: string; appendSiteName: boolean } {
    const stored = (this.aiPriceGuide?.title_tag ?? '').trim();
    const base = stripAuthoredPriceTail(
      stored,
      this.seoCountryConfig.authoredPriceLeadIns,
    );

    // A figure that survived the strip is not a price tail — it is part of the
    // subject («12 kvm badeværelse»). Appending would put two amounts on one
    // SERP line, which is worse than either alone.
    const token = containsPriceFigure(base)
      ? null
      : this.openSentence(
          formatTitlePriceToken(
            this.storedDisplayPrice(),
            this.seoCountryConfig.priceToken,
          ),
          base,
        );

    const composed = composeTitleWithPrice(
      base,
      token,
      ` - ${this.env.appName}`,
    );

    // Never trade away a price the page already publishes. On a stem too long
    // for even a brandless token, composeTitleWithPrice returns the bare stem —
    // and since the stored price lived in the tail this strip just removed,
    // shipping that stem would DELETE a figure from 953 pages' SERP line. Those
    // keep their untruncated original: no better than today, but no worse.
    if (containsPriceFigure(stored) && !containsPriceFigure(composed.title)) {
      return { title: stored, appendSiteName: true };
    }
    return composed;
  }

  /**
   * `price_guide` in the shape the shared title/description formatters read.
   *
   * `confidence: 'medium'` is a deliberate ceiling, not a placeholder: these
   * figures are aggregated from a single AI writer run (every value in the
   * collection dates to February 2025), so they may be quoted as a from-price
   * but must never be published as «typisk pris», which is what `high` unlocks.
   * `unit` is per-task — the guides price whole jobs, never hours.
   */
  private storedDisplayPrice(): DisplayPrice | undefined {
    const guide = this.aiPriceGuide?.price_guide;
    if (!guide || !(guide.min > 0)) {
      return undefined;
    }
    return {
      min: guide.min,
      middle: guide.middle,
      max: guide.max,
      unit: 'task',
      origin: 'ai-price-guide',
      confidence: 'medium',
    };
  }

  /** Capitalise the token when it starts a new sentence after the stem. */
  private openSentence(token: string | null, base: string): string | null {
    if (!token || !SENTENCE_END.test(base)) {
      return token;
    }
    // The token carries its own leading space; index 1 is its first letter.
    return ` ${token.charAt(1).toUpperCase()}${token.slice(2)}`;
  }

  public override setBreadCrumb() {
    this.partsOfUrl = setBreadCrumb({
      entity: this.aiPriceGuide!,
      hubIndex: this.seg.suffix.hubIndex,
      url: this.router.url,
    });
  }

  public override fetchPriceGuide() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        // On the bare-suffix route (`/:headCategory/pris-data`) there is no
        // guideSlug param — the head category doubles as the guide slug.
        this.slug = params.get('guideSlug') ?? params.get('headCategory')!;
        this.headCategory = params.get('headCategory')!;
        const subCategory = params.get('subCategory') || undefined;
        this.priceGuideService
          .fetchAIPriceGuideBySlug(this.slug, this.headCategory, subCategory)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: AIPriceGuide) => {
              if (
                res.canonicalPath &&
                this.redirectToCanonical(res.canonicalPath)
              ) {
                return;
              }
              this.aiPriceGuide = new AIPriceGuide(res);
              // Sanitize the rich text content before assigning
              if (this.aiPriceGuide?.content?.rich_text) {
                const sanitizedContent = this.sanitizer.sanitize(
                  SecurityContext.HTML,
                  this.aiPriceGuide.content.rich_text,
                );
                this.aiPriceGuide.content.rich_text = sanitizedContent || '';
              }
              this.priceGuide = {
                _id: res._id,
                title: res.title,
                imgUrl: res.imgUrl,
                price_guide: res.price_guide,
                content: res.content,
                question: res.question,
                answer: res.answer,
                meta_description: res.content.sub_header,
                heroImg: res?.heroImg || '',
                imgAltAttribute: res.imgAltAttribute || '',
              };
              // Reset cached values when new data arrives
              this._categoryTitle = '';
              this._whoCanHireSection = { body: '', title: '' };
              this._faqSection = [];
              this._marketInsights = { title: '', stats: null, content: '' };
              this._richTextSection = { title: '', content: '' };
              this._similarTasks = [];

              this.injectSchemaGuide();

              if (this.isBrowser && this.aiPriceGuide?.content?.rich_text) {
                const modifiedHtmlString = this.addIDsToH2s(
                  this.aiPriceGuide.content.rich_text,
                );
                this.priceGuide.content.rich_text =
                  this.sanitizer.bypassSecurityTrustHtml(modifiedHtmlString);
                this.h2Texts = this.getH2FromHtmlString(modifiedHtmlString);
              }

              this.updateTaskOfferUsers();
              this.answer = this.ensureEndingDot(
                this.aiPriceGuide?.answer ?? '',
              );
              this.setMetaDetails();
              this.initializeRouteMap();
              this.getRelatedPriceGuides(this.aiPriceGuide);
              this.cdr.markForCheck();
            },
            error: (err) => {
              if (err.status === 404) {
                if (this.responseInit) {
                  this.responseInit.status = 404;
                }
                // skipLocationChange: keep the requested URL so the SSR engine
                // renders the error page with the 404 status instead of
                // attempting an invalid non-3xx redirect.
                return this.router.navigate(['404'], {
                  skipLocationChange: true,
                });
              }
              if (this.responseInit) {
                this.responseInit.status = 503;
              }
              return;
            },
          });
      });
  }

  /**
   * The API sets canonicalPath when the URL's categories don't match the
   * guide's actual categories. A JSON endpoint cannot 301 to a frontend path
   * (fetch follows the Location on the API host and gets the SPA's HTML), so
   * the page-level redirect is issued here instead.
   */
  private redirectToCanonical(canonicalPath: string): boolean {
    const currentPath = this.router.url.split('?')[0];
    if (currentPath === canonicalPath) {
      return false;
    }
    if (this.isBrowser) {
      this.router.navigateByUrl(canonicalPath);
    } else if (this.responseInit) {
      this.responseInit.status = 301;
      this.responseInit.headers = { Location: canonicalPath };
    }
    return true;
  }

  private getRelatedPriceGuides(guide: AIPriceGuide) {
    if (!guide?.relatedCategory?.asCat && !guide.relatedVertical?.asCat) {
      return;
    }
    this.priceGuideService
      .fetchRelatedAIPriceGuides(
        Number(guide?.relatedCategory?.asCat ?? guide.relatedVertical?.asCat),
      )
      .subscribe((relatedGuides) => {
        this.relatedGuides = relatedGuides.map((guide: RelatedGuides) => {
          return {
            ...guide,
            finalSlug: `${EntityCategoryPreFixHelper(guide)}/${guide.slug}/${
              this.seg.suffix.aiPriceGuide
            }`,
          };
        });
        this.cdr.markForCheck();
      });
  }

  public injectSchemaGuide() {
    try {
      if (this.aiPriceGuide) {
        this.injectBreadCrumbSchema();
        // Ensure partsOfUrl is set before creating breadcrumb schema
        if (!this.partsOfUrl.length) {
          this.setBreadCrumb();
        }
        if (this.partsOfUrl.length) {
          this.breadCrumbSchema =
            this.seoService.setAIPriceGuideBreadCrumbSchema(this.partsOfUrl);
        }
      }
    } catch (err) {}
  }

  /**
   * The `Service` node, with its `Offer`s.
   *
   * The bid figures here are correct and were verified against live pages; what
   * this rebuild fixes is everything around them, per D27 — markup describes
   * what the page shows, empty and zero-valued members are omitted rather than
   * emitted, and nodes name the entity they actually refer to.
   */
  public override injectBreadCrumbSchema() {
    // `filteredTaskOffers` is what the page renders. The markup was mapping the
    // RAW list, so a bid from a deleted account published as «Deleted har givet
    // bud nr. 2» — a named offer appearing nowhere on the page. Numbering is
    // taken from the filtered list too, so the markup counts 1..n exactly as
    // the visible list does.
    const offers = this.filteredTaskOffers
      // A zero or missing bid is not a price; Google reads `price: 0` as free.
      .filter((o: TaskOffer) => Number(o?.priceBid) > 0)
      .map((o: TaskOffer, i: number) => ({
        '@type': 'Offer',
        name: this.t
          .transform('GUIDES.AI_PRICE_GUIDE.OFFER_NAME')
          .replace(/\{\{name\}\}/g, o?.user?.firstName ?? '')
          .replace(/\{\{n\}\}/g, String(i + 1)),
        price: o.priceBid,
        priceCurrency: this.localization.currencyCode().toUpperCase(),
      }));

    // The answer, and only the answer. The old template appended its own «.» to
    // copy that already ended in one and then bolted on «{{count}} brugere har
    // givet følgende bud» — a sentence about the bid list, not a description of
    // the service, and one that contradicted the filtered list beside it.
    const answer = this.aiPriceGuide?.answer?.trim();
    const name = this.aiPriceGuide?.title?.trim();

    this.priceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      ...(name ? { name } : {}),
      ...(answer ? { description: this.ensureEndingDot(answer) } : {}),
      provider: {
        '@type': 'Organization',
        name: this.env.appName,
        // The Organization that provides the service, not the page describing
        // it. `${domain}${router.url}` gave all 9,870 guides a different
        // «Handyhand», each identified by the URL of one price guide.
        url: this.env.domain,
      },
      ...(offers.length ? { offers } : {}),
    };
  }

  public override addIDsToH2s(htmlString: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const h2s = doc.getElementsByTagName('h2');
    const h3s = doc.getElementsByTagName('h3');

    Array.from(h2s).forEach((h2, i) => {
      h2.id = `h2-${i}`;
    });

    Array.from(h3s).forEach((h3, i) => {
      h3.id = `h3-${i}`;
    });
    return new XMLSerializer().serializeToString(doc);
  }

  public override getH2FromHtmlString(
    htmlString: string,
  ): { id: string; text: string; level: number }[] {
    const sanitizedHtml =
      this.sanitizer.sanitize(SecurityContext.HTML, htmlString) || '';
    const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');

    const headings = [];

    // Get h2 elements
    const h2Elements = doc.getElementsByTagName('h2');
    headings.push(
      ...Array.from(h2Elements).map((h2) => ({
        id: h2.id,
        text: h2.textContent || '',
        level: 2,
      })),
    );

    // Get h3 elements
    const h3Elements = doc.getElementsByTagName('h3');
    headings.push(
      ...Array.from(h3Elements).map((h3) => ({
        id: h3.id,
        text: h3.textContent || '',
        level: 3,
      })),
    );

    // Sort by position in document
    return headings.sort((a, b) => {
      const aEl = doc.getElementById(a.id);
      const bEl = doc.getElementById(b.id);
      if (!aEl || !bEl) return 0;
      return aEl.compareDocumentPosition(bEl) & Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1;
    });
  }

  get tableOfContents() {
    const sections = [];

    // Add main question if exists
    if (this.aiPriceGuide?.question) {
      sections.push({
        id: 'main-question',
        text: this.priceGuide.question,
        level: 2,
      });
    }

    // Add rich text section if exists
    if (this.richTextSection.title) {
      sections.push({
        id: 'rich-text',
        text: this.richTextSection.title,
        level: 2,
      });
    }

    // Add task example section if exists
    if (this.aiPriceGuide?.task) {
      sections.push({
        id: 'task-example',
        text: this.t.transform('PAGES.PRICE_GUIDE.TASK_EXAMPLE'),
        level: 3,
      });
    }

    // Add market insights if exists
    if (this.marketInsights.title) {
      sections.push({
        id: 'market-insights',
        text: this.marketInsights.title,
        level: 2,
      });
    }

    // Add similar tasks section if exists
    if (this.similarTasks.length > 0) {
      sections.push({
        id: 'similar-tasks',
        text: this.t.transform('PAGES.PRICE_GUIDE.SIMILAR_TASKS_TITLE'),
        level: 2,
      });
    }

    // Add who can hire section if exists
    if (this.whoCanHireSection.title) {
      sections.push({
        id: 'who-can-hire',
        text: this.whoCanHireSection.title,
        level: 2,
      });
    }

    // Add video section if exists
    if (this.videoLoaded && this.youtubeVidTitle) {
      sections.push({
        id: 'video-section',
        text: this.youtubeVidTitle,
        level: 2,
      });
    }

    // Add FAQ section if exists
    if (this.faqSection.length > 0) {
      sections.push({
        id: 'faq-section',
        text: this.t.transform('COMPONENTS.FAQ.Q3'),
        level: 2,
      });
    }

    return sections;
  }

  get mapUrl(): string {
    if (!this.routeMapUrl) {
      const cachedUrl = this.getCachedMapUrl();
      if (cachedUrl) {
        this.routeMapUrl = cachedUrl;
      } else {
        this.initializeRouteMap();
      }
    }
    return this.routeMapUrl;
  }

  private getCacheKey(): any {
    if (!this.aiPriceGuide?.task?.id) return '';

    // Include route parameters in the cache key
    const routeParams = {
      slug: this.slug,
      headCategory: this.headCategory,
      taskId: this.aiPriceGuide.task.id,
      fromAddress: this.aiPriceGuide.task.extendedData?.fromAddress?.city,
      toAddress: this.aiPriceGuide.task.extendedData?.toAddress?.city,
    };

    try {
      return this.MAP_CACHE_PREFIX + JSON.stringify(routeParams);
    } catch (err) {}
  }

  private getCachedMapUrl(): string | null {
    if (!this.isBrowser) return null;

    const cacheKey = this.getCacheKey();
    if (!cacheKey) return null;

    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    try {
      const { url, timestamp } = JSON.parse(cached);
      const expiryTime =
        new Date(timestamp).getTime() +
        this.CACHE_EXPIRY_HOURS * 60 * 60 * 1000;

      if (Date.now() > expiryTime) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return url;
    } catch (error) {
      return null;
    }
  }

  private initializeRouteMap() {
    if (!this.aiPriceGuide?.task?.extendedData) {
      return;
    }

    const { fromAddress, toAddress } = this.aiPriceGuide.task.extendedData;

    if (!fromAddress || !toAddress) {
      return;
    }
  }

  get extendedAddressData() {
    const extendedData = this.aiPriceGuide?.task?.extendedData;
    const task = this.aiPriceGuide?.task;

    if (!extendedData?.fromAddress?.city || !extendedData?.toAddress?.city) {
      return null;
    }

    const pricePerKm =
      task?.budget && extendedData.distance
        ? Math.round(task.budget / extendedData.distance)
        : null;

    return {
      fromCity: extendedData.fromAddress.city,
      toCity: extendedData.toAddress.city,
      distance: extendedData.distance,
      budget: task?.budget,
      pricePerKm,
    };
  }

  private updateTaskOfferUsers() {
    const users = new Map();

    // Create a unique key from user properties
    const getUserKey = (user: any) => {
      const firstName = (user.firstName || '').toLowerCase().trim();
      const profileImg = (user.profileImg || '').toLowerCase().trim();
      const rating = user.rating || 0;
      const id = user.id || '';
      return `${id}-${firstName}-${rating}-${profileImg}`;
    };

    // Helper function to determine if two users are likely the same
    const isSameUser = (user1: any, user2: any) => {
      return user1.id === user2.id;
    };

    // Helper function to check if user should be included
    const shouldIncludeUser = (user: any) => {
      return (
        user && user.firstName && user.firstName.toLowerCase() !== 'deleted'
      );
    };

    // Add users from the main task if it has offers
    if (this.aiPriceGuide?.task?.offers) {
      this.aiPriceGuide.task.offers.forEach((offer) => {
        if (offer.user && shouldIncludeUser(offer.user)) {
          const key = getUserKey(offer.user);
          const existingUser = Array.from(users.values()).find((u) =>
            isSameUser(u, offer.user),
          );
          if (!existingUser) {
            users.set(key, {
              ...offer.user,
              rating: offer.user.rating || 0,
              categories: offer.user.categories || [],
            });
          }
        }
      });
    }

    // Add users from similar tasks
    if (this.similarTasks) {
      this.similarTasks.forEach((task) => {
        task.offers?.forEach((offer: any) => {
          if (offer.user && shouldIncludeUser(offer.user)) {
            const key = getUserKey(offer.user);
            const existingUser = Array.from(users.values()).find((u) =>
              isSameUser(u, offer.user),
            );
            if (!existingUser) {
              users.set(key, {
                ...offer.user,
                rating: offer.user.rating || 0,
                categories: offer.user.categories || [],
              });
            }
          }
        });
      });
    }

    // Convert to array and sort
    this._taskOfferUsers = Array.from(users.values())
      .sort((a, b) => {
        // First sort by profile image existence
        if (a.profileImg && !b.profileImg) return -1;
        if (!a.profileImg && b.profileImg) return 1;

        return (b.rating || 0) - (a.rating || 0);
      })
      .slice(0, 6);
  }

  get taskOfferUsers(): any[] {
    return this._taskOfferUsers;
  }

  getUserCategories(user: any): string {
    if (!user.categories?.length) return '';
    return user.categories
      .slice(0, 3)
      .map((cat: any) => cat.title)
      .join(', ');
  }

  formatRating(rating: number): string {
    return rating ? rating.toFixed(1) : '0.0';
  }

  trackByFaqId(index: number, faq: any): string {
    return faq?.id || index.toString();
  }

  trackByOfferId(index: number, offer: any): string {
    return offer?.createdAt || index.toString();
  }

  trackByUserId(index: number, user: any): string {
    return user?.id || index.toString();
  }

  override ngOnInit() {
    // Check for pending redirects before doing anything else
    if (!isPlatformBrowser(this.platformId)) {
      const pendingRedirect = this.redirectHandler.getPendingRedirect();
      if (pendingRedirect) {
        const redirected = this.redirectHandler.executePendingRedirect();
        if (redirected) {
          return;
        } else {
          this.redirectHandler.clearPendingRedirect();
        }
      }
    }

    super.ngOnInit();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._categoryTitle = '';
    this._whoCanHireSection = { body: '', title: '' };
    this._faqSection = [];
    this._marketInsights = { title: '', stats: null, content: '' };
    this._richTextSection = { title: '', content: '' };
    this._similarTasks = [];
    this._taskOfferUsers = [];
  }

  scrollToTop(): void {
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
